import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { setWarehouse as updateActiveWarehouse } from '../../Redux/Slices/userSlice';
import { 
    Loader2, Save, MapPin, AlertCircle, Database, 
    RefreshCw, Trash2, Settings as SettingsIcon,
    Palette, ShieldAlert, Cpu, HardDrive, 
    CheckCircle2, ChevronRight, LayoutDashboard,
    RotateCcw, Award, Truck, GitFork, ToggleLeft, ToggleRight,
    CreditCard, DollarSign, Banknote, Coins, Building2, Package, Printer, Zap, Lock
} from 'lucide-react';
import { db } from '../../db';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import '../Admin/SalesOrder.css';
import { useLegacyTheme } from '../../hooks/useLegacyTheme';
import { useCustomShortcuts, ACTION_LABELS, getShortcutStringFromEvent } from '../../hooks/useCustomShortcuts';
import { useBoxSetting } from '../../hooks/useBoxSetting';
import { usePrintJobSetting } from '../../hooks/usePrintJobSetting';
import { useClosingEntrySettings } from '../../hooks/useClosingEntrySettings';
import { useItemSubgroupSetting } from '../../hooks/useItemSubgroupSetting';
import { useMultiBranchSetting } from '../../hooks/useMultiBranchSetting';
import { useCashierSecretKeySetting } from '../../hooks/useCashierSecretKeySetting';
const Settings = () => {
    const dispatch = useDispatch();
    const activeWarehouse = useSelector((state) => state.user.warehouse);
    const company = useSelector((state) => state.user.company);
    const session = useSelector((state) => state.user.session);

    const [warehouses, setWarehouses] = useState([]);
    const [selectedWarehouse, setSelectedWarehouse] = useState(activeWarehouse);
    const [loading, setLoading] = useState(true);

    // Multi-Branch Feature Toggle
    const { enableMultiBranch, updateMultiBranchSetting } = useMultiBranchSetting();

    // Box UOM & Selling Box Price Feature Toggle
    const { enableBox, updateBoxSetting } = useBoxSetting();
    const { enableSecretKey, updateSecretKeySetting } = useCashierSecretKeySetting();

    // Print Job & Fast Print Feature Toggle
    const { enablePrintJobs, updatePrintJobSetting } = usePrintJobSetting();

    const { 
        enableDenomination, updateDenominationSetting, 
        enableTelephoneBalance, updateTelephoneBalanceSetting 
    } = useClosingEntrySettings();

    // Item Subgroup Custom Field Toggle
    const { enableItemSubgroup, updateItemSubgroupSetting } = useItemSubgroupSetting();

    // Shortcuts Hook & State
    const { shortcuts, updateShortcut, resetAllShortcuts } = useCustomShortcuts();
    const [recordingAction, setRecordingAction] = useState(null); // { page, actionId }
    const [activeShortcutTab, setActiveShortcutTab] = useState('pos_home');

    // Theme Hook
    const { legacySubTheme, isGreen, themeColor, themeColorHover, themeLight, toggleTheme } = useLegacyTheme();

    useEffect(() => {
        if (!recordingAction) return;

        const handleRecordingKeyDown = (e) => {
            e.preventDefault();
            e.stopPropagation();

            // If Escape is pressed alone, cancel recording
            if (e.key === 'Escape' && !e.ctrlKey && !e.altKey && !e.shiftKey) {
                setRecordingAction(null);
                const Toast = Swal.mixin({
                    toast: true, position: 'top-end', showConfirmButton: false, timer: 1500
                });
                Toast.fire({
                    icon: 'info',
                    title: 'Recording cancelled'
                });
                return;
            }

            const shortcutStr = getShortcutStringFromEvent(e);
            if (shortcutStr) {
                updateShortcut(recordingAction.page, recordingAction.actionId, shortcutStr);
                setRecordingAction(null);
                
                const Toast = Swal.mixin({
                    toast: true, position: 'top-end', showConfirmButton: false, timer: 2000,
                });
                Toast.fire({
                    icon: 'success',
                    title: `Shortcut updated: ${shortcutStr}`
                });
            }
        };

        window.addEventListener('keydown', handleRecordingKeyDown, true);
        return () => window.removeEventListener('keydown', handleRecordingKeyDown, true);
    }, [recordingAction, updateShortcut]);


    useEffect(() => {
        const fetchWarehouses = async () => {
            try {
                const res = await fetch(`/api/method/future_retail_pos.api.reports.get_company_warehouses?company=${encodeURIComponent(company)}`, {
                    headers: { 'X-Frappe-SID': session },
                    credentials: 'include'
                });
                const data = await res.json();
                const list = data.message || [];
                setWarehouses(list);
            } catch (err) {
                console.error("Failed to fetch warehouses:", err);
            } finally {
                setLoading(false);
            }
        };

        if (company && session) {
            fetchWarehouses();
        }
    }, [company, session]);



    // Loyalty Program Settings
    const [enableLoyaltyProgram, setEnableLoyaltyProgram] = useState(() => {
        const cached = localStorage.getItem('enable_loyalty_program');
        return cached !== null ? cached === 'true' || cached === '1' : false;
    });
    const [conversionFactor, setConversionFactor] = useState(0.01);
    const [maxLoyaltyRedemption, setMaxLoyaltyRedemption] = useState(0);
    const [savingLoyalty, setSavingLoyalty] = useState(false);

    useEffect(() => {
        const fetchLoyaltySettings = async () => {
            try {
                const res = await fetch('/api/method/future_retail_pos.api.common.get_loyalty_settings', {
                    headers: { 'X-Frappe-SID': session },
                    credentials: 'include'
                });
                const data = await res.json();
                if (data.message) {
                    const isEnabled = data.message.enable_loyalty_program !== 0;
                    setEnableLoyaltyProgram(isEnabled);
                    localStorage.setItem('enable_loyalty_program', isEnabled ? 'true' : 'false');
                    setConversionFactor(data.message.conversion_factor || 0.01);
                    setMaxLoyaltyRedemption(data.message.max_redemption_amount || 0);
                }
            } catch (err) {
                console.error("Failed to fetch loyalty settings:", err);
            }
        };

        if (session) {
            fetchLoyaltySettings();
        }
    }, [session]);

    const handleSaveLoyaltySettings = async () => {
        setSavingLoyalty(true);
        try {
            const res = await fetch('/api/method/future_retail_pos.api.common.update_loyalty_settings', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Frappe-SID': session
                },
                credentials: 'include',
                body: JSON.stringify({
                    enable_loyalty_program: enableLoyaltyProgram ? 1 : 0,
                    conversion_factor: parseFloat(conversionFactor),
                    max_redemption_amount: parseFloat(maxLoyaltyRedemption)
                })
            });
            const data = await res.json();
            if (data.message && data.message.status === 'success') {
                localStorage.setItem('enable_loyalty_program', enableLoyaltyProgram ? 'true' : 'false');
                window.dispatchEvent(new Event('loyalty_setting_changed'));
                window.dispatchEvent(new Event('storage'));
                Swal.fire({
                    icon: 'success',
                    title: 'Loyalty Rules Saved',
                    text: 'Loyalty program settings updated across all POS terminals.',
                    timer: 2000,
                    showConfirmButton: false
                });
            } else {
                throw new Error("Failed to update loyalty settings.");
            }
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Save Failed',
                text: err.message
            });
        } finally {
            setSavingLoyalty(false);
        }
    };

    // Purchase Stock Workflow Settings
    const [purchaseWorkflow, setPurchaseWorkflow] = useState('PR_FIRST'); // 'PR_FIRST' or 'PI_DIRECT'

    useEffect(() => {
        const fetchPurchaseWorkflow = async () => {
            try {
                const res = await axios.get('/api/method/future_retail_pos.api.purchase.get_purchase_stock_settings');
                const mode = res.data?.message?.workflow_mode || 'PR_FIRST';
                setPurchaseWorkflow(mode);
                localStorage.setItem('purchase_stock_workflow', mode);
            } catch (err) {
                console.error('Failed to fetch purchase stock settings:', err);
            }
        };
        fetchPurchaseWorkflow();
    }, []);

    const handleSavePurchaseWorkflow = async () => {
        try {
            await axios.post('/api/method/future_retail_pos.api.common.update_purchase_stock_settings', {
                workflow_mode: purchaseWorkflow
            });
            localStorage.setItem('purchase_stock_workflow', purchaseWorkflow);
        } catch (err) {
            console.error('Failed to update purchase stock settings:', err);
        }
    };

    const DEFAULT_PAYMENT_MODES = [
        { name: 'Cash', enabled: 1, type: 'Cash' },
        { name: 'Card', enabled: 1, type: 'Bank' },
        { name: 'InstaPay', enabled: 1, type: 'Bank' },
        { name: 'Credit', enabled: 1, type: 'Credit' },
        { name: 'Bank', enabled: 1, type: 'Bank' },
        { name: 'Cheque', enabled: 1, type: 'Bank' },
        { name: 'Wire Transfer', enabled: 1, type: 'Bank' }
    ];

    // Mode of Payment Settings
    const [paymentModes, setPaymentModes] = useState(() => {
        try {
            const cached = localStorage.getItem('enabled_payment_modes');
            if (cached) {
                const parsed = JSON.parse(cached);
                if (Array.isArray(parsed) && parsed.length > 0) return parsed;
            }
        } catch (e) {
            console.error(e);
        }
        return DEFAULT_PAYMENT_MODES;
    });
    const [savingPaymentModes, setSavingPaymentModes] = useState(false);

    useEffect(() => {
        const fetchPaymentModes = async () => {
            try {
                const res = await axios.get('/api/method/future_retail_pos.api.common.get_mode_of_payment_settings', { withCredentials: true });
                if (res.data?.message?.status === 'success' && Array.isArray(res.data.message.modes)) {
                    const modes = res.data.message.modes;
                    setPaymentModes(modes);
                    localStorage.setItem('enabled_payment_modes', JSON.stringify(modes));
                }
            } catch (err) {
                console.warn("Backend payment mode fetch skipped, using local settings:", err);
            }
        };
        fetchPaymentModes();
    }, []);

    const togglePaymentMode = (name) => {
        setPaymentModes(prev => {
            const updated = prev.map(m => m.name === name ? { ...m, enabled: m.enabled ? 0 : 1 } : m);
            localStorage.setItem('enabled_payment_modes', JSON.stringify(updated));
            return updated;
        });
    };

    const handleSavePaymentModes = async () => {
        setSavingPaymentModes(true);
        try {
            localStorage.setItem('enabled_payment_modes', JSON.stringify(paymentModes));
            try {
                await axios.post('/api/method/future_retail_pos.api.common.update_mode_of_payment_settings', {
                    modes: paymentModes
                }, { withCredentials: true });
            } catch (backendErr) {
                console.warn("Backend payment mode sync skipped:", backendErr);
            }
            Swal.fire({
                icon: 'success',
                title: 'Payment Modes Saved',
                text: 'Payment methods updated across all POS terminals.',
                timer: 1500,
                showConfirmButton: false
            });
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Save Failed',
                text: err.message
            });
        } finally {
            setSavingPaymentModes(false);
        }
    };

    const handleSave = () => {
        if (!selectedWarehouse) {
            Swal.fire({
                icon: 'error',
                title: 'Required Field',
                text: 'Please select a primary warehouse.',
                confirmButtonColor: themeColor
            });
            return;
        }

        dispatch(updateActiveWarehouse(selectedWarehouse));
        localStorage.setItem('enable_multi_branch', enableMultiBranch ? 'true' : 'false');
        handleSaveLoyaltySettings();
        handleSavePurchaseWorkflow();
        Swal.fire({
            icon: 'success',
            title: 'Settings Applied',
            text: 'Terminal configuration and purchase stock workflow updated.',
            timer: 2000,
            showConfirmButton: false
        });
    };

    const handleForceReset = async () => {
        if (!navigator.onLine) {
            Swal.fire({
                title: 'Connection Required',
                text: 'System must be online to reconstruct local cache after reset.',
                icon: 'warning',
                confirmButtonColor: themeColor
            });
            return;
        }

        const unsyncedCount = await db.invoices.where('is_synced').equals(0).count();
        if (unsyncedCount > 0) {
            const result = await Swal.fire({
                title: 'Data Collision Risk!',
                text: `You have ${unsyncedCount} unsynced transactions. Resetting now will cause permanent local data loss.`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#dc2626',
                cancelButtonColor: '#94a3b8',
                confirmButtonText: 'I Understand, Wipe Anyway'
            });
            if (!result.isConfirmed) return;
        } else {
            const result = await Swal.fire({
                title: 'Purge Local Cache?',
                text: 'This will reconstruct the items, customers, and pricing databases from scratch.',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Commence Reset',
                confirmButtonColor: themeColor
            });
            if (!result.isConfirmed) return;
        }

        try {
            Swal.fire({
                title: 'Executing Purge...',
                text: 'Reconstructing environment...',
                allowOutsideClick: false,
                didOpen: () => Swal.showLoading()
            });

            await Promise.all([
                db.items.clear(),
                db.customers.clear(),
                db.tax_templates.clear(),
                db.sync_log.clear(),
                db.invoices.clear()
            ]);

            localStorage.removeItem('last_item_sync_time');

            Swal.fire({
                icon: 'success',
                title: 'Environment Restored',
                text: 'The station will perform a fresh synchronization on the next cycle.',
                confirmButtonColor: themeColor
            });
        } catch (err) {
            Swal.fire('Restoration Failed', err.message, 'error');
        }
    };

    if (loading) return (
        <div className="so-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center' }}>
                <Loader2 size={40} className="animate-spin" style={{ color: themeColor, margin: '0 auto' }} />
                <p style={{ marginTop: '1rem', fontWeight: 700, color: 'var(--so-text-muted)', letterSpacing: '0.05em' }}>LOADING CONFIGURATION...</p>
            </div>
        </div>
    );

    return (
        <div className="so-page">
            {/* Header */}
            <div className="so-page-header">
                <div>
                    <h1 className="so-page-title">
                        <SettingsIcon size={22} color={themeColor} />
                        Terminal Configuration
                    </h1>
                    <p className="so-page-subtitle">Manage regional station settings, stock sources, and data integrity.</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <button
                        onClick={toggleTheme}

                        style={{
                            display: 'flex', alignItems: 'center', gap: '0.4rem',
                            padding: '0.45rem 1rem', background: '#f8fafc',
                            border: `1.5px solid ${themeColor}`, borderRadius: '0.5rem',
                            fontSize: '0.75rem', fontWeight: 800, color: themeColor,
                            cursor: 'pointer', transition: 'all 0.2s',
                            textTransform: 'uppercase', letterSpacing: '0.04em'
                        }}
                    >
                        <Palette size={14} /> {isGreen ? 'BLUE' : 'GREEN'}


                    </button>
                    <div style={{ width: '1px', height: '24px', background: '#e2e8f0', margin: '0 0.25rem' }}></div>
                    <button className="so-btn-primary" onClick={handleSave}>
                        <Save size={18} /> Apply Changes
                    </button>
                </div>
            </div>

            <div className="so-layout">
                <main className="so-content" style={{ padding: '2rem', display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '2rem' }}>
                    
                    {/* Primary Settings Column */}
                    <div style={{ gridColumn: 'span 7', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        
                        {/* Warehouse Mapping Card */}
                        <div className="so-table-card" style={{ padding: '1.5rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.5rem' }}>
                                <div style={{ background: `${themeColor}15`, color: themeColor, padding: '8px', borderRadius: '10px' }}>
                                    <MapPin size={18} />
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--so-text-heading)', margin: 0 }}>Stock Source</h3>
                                    <p style={{ fontSize: '0.7rem', color: 'var(--so-text-muted)', margin: 0 }}>Direct the station to a specific regional warehouse or stock point.</p>
                                </div>
                            </div>

                            <div style={{ marginBottom: '1.25rem' }}>
                                <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--so-text-muted)', marginBottom: '8px' }}>Primary Warehouse</label>
                                <div style={{ position: 'relative' }}>
                                    <select
                                        value={selectedWarehouse}
                                        onChange={(e) => setSelectedWarehouse(e.target.value)}
                                        className="so-filter-select"
                                        style={{ height: '48px', fontSize: '0.85rem', fontWeight: 600, paddingLeft: '1rem', background: '#f8fafc' }}
                                    >
                                        <option value="">-- DEFAULT SYSTEM ALLOCATION --</option>
                                        {warehouses.map(w => (
                                            <option key={w.name} value={w.name}>{w.warehouse_name || w.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <p style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: '8px', fontStyle: 'italic' }}>
                                    Changing the warehouse redirects all sales, item searches, and stock counts to the selected location instantly.
                                </p>
                            </div>
                        </div>

                                            {/* Cashier Secret Key Verification Toggle Card */}
                    <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex items-center justify-between">
                        <div className="flex items-start gap-4">
                            <div className="p-3 bg-amber-50 text-amber-600 rounded-xl">
                                <Lock size={24} />
                            </div>
                            <div>
                                <h3 className="text-base font-black text-slate-800">Cashier Secret Key Verification</h3>
                                <p className="text-xs text-slate-500 mt-1 max-w-xl">
                                    Require manager PIN / Secret Key when applying customer discounts or sensitive POS overrides. (Default: Disabled).
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => updateSecretKeySetting(!enableSecretKey)}
                            className="p-2 hover:bg-slate-50 rounded-xl transition-all border-none bg-transparent cursor-pointer"
                            title={enableSecretKey ? "Disable Cashier Secret Key" : "Enable Cashier Secret Key"}
                        >
                            {enableSecretKey ? (
                                <ToggleRight size={32} color="#f59e0b" />
                            ) : (
                                <ToggleLeft size={32} color="#94a3b8" />
                            )}
                        </button>
                    </div>

{/* Multi-Branch Features Toggle Card */}
                        <div className="so-table-card" style={{ padding: '1.5rem', border: `1.5px solid ${enableMultiBranch ? '#6366f130' : '#e2e8f0'}` }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ background: enableMultiBranch ? '#6366f115' : '#f1f5f9', color: enableMultiBranch ? '#6366f1' : '#94a3b8', padding: '8px', borderRadius: '10px' }}>
                                        <GitFork size={20} />
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--so-text-heading)', margin: 0 }}>Multi-Branch Features (Request Stock & Branch Stock)</h3>
                                        <p style={{ fontSize: '0.7rem', color: 'var(--so-text-muted)', margin: 0 }}>Enable or disable inter-branch stock requests and multi-warehouse breakdown popups.</p>
                                    </div>
                                </div>
                                <div 
                                    onClick={() => {
                                        updateMultiBranchSetting(!enableMultiBranch);
                                    }} 
                                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                                >
                                    {enableMultiBranch ? (
                                        <ToggleRight size={32} color="#6366f1" />
                                    ) : (
                                        <ToggleLeft size={32} color="#94a3b8" />
                                    )}
                                    <span style={{ fontSize: '0.75rem', fontWeight: 800, color: enableMultiBranch ? '#6366f1' : '#64748b' }}>
                                        {enableMultiBranch ? 'ENABLED' : 'DISABLED'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Box UOM & Selling Box Price Features Toggle Card */}
                        <div className="so-table-card" style={{ padding: '1.5rem', border: `1.5px solid ${enableBox ? '#8b5cf630' : '#e2e8f0'}` }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ background: enableBox ? '#8b5cf615' : '#f1f5f9', color: enableBox ? '#8b5cf6' : '#94a3b8', padding: '8px', borderRadius: '10px' }}>
                                        <Package size={20} />
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--so-text-heading)', margin: 0 }}>Box UOM & Selling Box Price Features</h3>
                                        <p style={{ fontSize: '0.7rem', color: 'var(--so-text-muted)', margin: 0 }}>Enable or disable Box packing, Box prices, and Box UOM fields across item master, billing, and orders.</p>
                                    </div>
                                </div>
                                <div 
                                    onClick={() => {
                                        updateBoxSetting(!enableBox);
                                    }} 
                                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                                >
                                    {enableBox ? (
                                        <ToggleRight size={32} color="#8b5cf6" />
                                    ) : (
                                        <ToggleLeft size={32} color="#94a3b8" />
                                    )}
                                    <span style={{ fontSize: '0.75rem', fontWeight: 800, color: enableBox ? '#8b5cf6' : '#64748b' }}>
                                        {enableBox ? 'ENABLED' : 'DISABLED'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Item Subgroup Custom Field Toggle Card */}
                        <div className="so-table-card" style={{ padding: '1.5rem', border: `1.5px solid ${enableItemSubgroup ? '#ec489930' : '#e2e8f0'}` }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ background: enableItemSubgroup ? '#ec489915' : '#f1f5f9', color: enableItemSubgroup ? '#ec4899' : '#94a3b8', padding: '8px', borderRadius: '10px' }}>
                                        <Package size={20} />
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--so-text-heading)', margin: 0 }}>Item Master: Item Subgroup Custom Field</h3>
                                        <p style={{ fontSize: '0.7rem', color: 'var(--so-text-muted)', margin: 0 }}>Enable or disable the custom Item Subgroup field when creating or editing items in Item Master.</p>
                                    </div>
                                </div>
                                <div 
                                    onClick={() => {
                                        updateItemSubgroupSetting(!enableItemSubgroup);
                                    }} 
                                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                                >
                                    {enableItemSubgroup ? (
                                        <ToggleRight size={32} color="#ec4899" />
                                    ) : (
                                        <ToggleLeft size={32} color="#94a3b8" />
                                    )}
                                    <span style={{ fontSize: '0.75rem', fontWeight: 800, color: enableItemSubgroup ? '#ec4899' : '#64748b' }}>
                                        {enableItemSubgroup ? 'ENABLED' : 'DISABLED'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Navbar Print Job & Fast Print Buttons Toggle Card */}
                        <div className="so-table-card" style={{ padding: '1.5rem', border: `1.5px solid ${enablePrintJobs ? '#0ea5e930' : '#e2e8f0'}` }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ background: enablePrintJobs ? '#0ea5e915' : '#f1f5f9', color: enablePrintJobs ? '#0ea5e9' : '#94a3b8', padding: '8px', borderRadius: '10px' }}>
                                        <Printer size={20} />
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--so-text-heading)', margin: 0 }}>Navbar Print Job & Fast Print Buttons</h3>
                                        <p style={{ fontSize: '0.7rem', color: 'var(--so-text-muted)', margin: 0 }}>Show or hide the Print Job and Fast Print quick action buttons in the top navbar on the POS billing screen.</p>
                                    </div>
                                </div>
                                <div 
                                    onClick={() => {
                                        updatePrintJobSetting(!enablePrintJobs);
                                    }} 
                                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                                >
                                    {enablePrintJobs ? (
                                        <ToggleRight size={32} color="#0ea5e9" />
                                    ) : (
                                        <ToggleLeft size={32} color="#94a3b8" />
                                    )}
                                    <span style={{ fontSize: '0.75rem', fontWeight: 800, color: enablePrintJobs ? '#0ea5e9' : '#64748b' }}>
                                        {enablePrintJobs ? 'ENABLED' : 'DISABLED'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* POS Closing: Counted Cash Denominations Toggle Card */}
                        <div className="so-table-card" style={{ padding: '1.5rem', border: `1.5px solid ${enableDenomination ? '#10b98130' : '#e2e8f0'}` }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ background: enableDenomination ? '#10b98115' : '#f1f5f9', color: enableDenomination ? '#10b981' : '#94a3b8', padding: '8px', borderRadius: '10px' }}>
                                        <Coins size={20} />
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--so-text-heading)', margin: 0 }}>POS Closing: Cash Denominations</h3>
                                        <p style={{ fontSize: '0.7rem', color: 'var(--so-text-muted)', margin: 0 }}>Show or hide the Counted UAE Cash Denominations grid input on the POS Closing Entry page.</p>
                                    </div>
                                </div>
                                <div 
                                    onClick={() => {
                                        updateDenominationSetting(!enableDenomination);
                                    }} 
                                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                                >
                                    {enableDenomination ? (
                                        <ToggleRight size={32} color="#10b981" />
                                    ) : (
                                        <ToggleLeft size={32} color="#94a3b8" />
                                    )}
                                    <span style={{ fontSize: '0.75rem', fontWeight: 800, color: enableDenomination ? '#10b981' : '#64748b' }}>
                                        {enableDenomination ? 'ENABLED' : 'DISABLED'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* POS Closing: Telephone Machine Balance Toggle Card */}
                        <div className="so-table-card" style={{ padding: '1.5rem', border: `1.5px solid ${enableTelephoneBalance ? '#f59e0b30' : '#e2e8f0'}` }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ background: enableTelephoneBalance ? '#f59e0b15' : '#f1f5f9', color: enableTelephoneBalance ? '#f59e0b' : '#94a3b8', padding: '8px', borderRadius: '10px' }}>
                                        <Building2 size={20} />
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--so-text-heading)', margin: 0 }}>POS Closing: Telephone Machine Balance</h3>
                                        <p style={{ fontSize: '0.7rem', color: 'var(--so-text-muted)', margin: 0 }}>Show or hide the Telephone Machine Balance / EMC Machine input section on the POS Closing Entry page.</p>
                                    </div>
                                </div>
                                <div 
                                    onClick={() => {
                                        updateTelephoneBalanceSetting(!enableTelephoneBalance);
                                    }} 
                                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                                >
                                    {enableTelephoneBalance ? (
                                        <ToggleRight size={32} color="#f59e0b" />
                                    ) : (
                                        <ToggleLeft size={32} color="#94a3b8" />
                                    )}
                                    <span style={{ fontSize: '0.75rem', fontWeight: 800, color: enableTelephoneBalance ? '#f59e0b' : '#64748b' }}>
                                        {enableTelephoneBalance ? 'ENABLED' : 'DISABLED'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Purchase Stock Workflow Configuration Card */}
                        <div className="so-table-card" style={{ padding: '1.5rem', border: `1.5px solid ${purchaseWorkflow === 'PR_FIRST' ? '#10b98130' : '#3b82f630'}` }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ background: purchaseWorkflow === 'PR_FIRST' ? '#10b98115' : '#3b82f615', color: purchaseWorkflow === 'PR_FIRST' ? '#10b981' : '#3b82f6', padding: '8px', borderRadius: '10px' }}>
                                        <Truck size={20} />
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--so-text-heading)', margin: 0 }}>Purchase Stock Workflow (PO ➔ PR ➔ PI)</h3>
                                        <p style={{ fontSize: '0.7rem', color: 'var(--so-text-muted)', margin: 0 }}>Configure when physical warehouse inventory is updated during procurement.</p>
                                    </div>
                                </div>
                                <span style={{ fontSize: '0.65rem', fontWeight: 900, color: purchaseWorkflow === 'PR_FIRST' ? '#059669' : '#2563eb', background: purchaseWorkflow === 'PR_FIRST' ? '#ecfdf5' : '#eff6ff', padding: '4px 10px', borderRadius: '6px', textTransform: 'uppercase', border: `1px solid ${purchaseWorkflow === 'PR_FIRST' ? '#a7f3d0' : '#bfdbfe'}` }}>
                                    {purchaseWorkflow === 'PR_FIRST' ? 'Stock at PR' : 'Stock at PI'}
                                </span>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                                {/* Option A: PO -> PR -> PI */}
                                <div
                                    onClick={() => setPurchaseWorkflow('PR_FIRST')}
                                    style={{
                                        border: `2px solid ${purchaseWorkflow === 'PR_FIRST' ? '#10b981' : '#e2e8f0'}`,
                                        background: purchaseWorkflow === 'PR_FIRST' ? '#f0fdf4' : '#ffffff',
                                        borderRadius: '0.75rem',
                                        padding: '1rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                        <span style={{ fontSize: '0.8rem', fontWeight: 900, color: purchaseWorkflow === 'PR_FIRST' ? '#047857' : '#334155' }}>
                                            PO ➔ PR ➔ PI
                                        </span>
                                        {purchaseWorkflow === 'PR_FIRST' && <CheckCircle2 size={16} color="#10b981" />}
                                    </div>
                                    <p style={{ fontSize: '0.68rem', fontWeight: 700, color: '#475569', margin: 0, marginBottom: '6px' }}>
                                        Stock Updated at Purchase Receipt (PR)
                                    </p>
                                    <p style={{ fontSize: '0.63rem', color: '#64748b', margin: 0, lineHeight: 1.3 }}>
                                        PR posts inventory ledger entries (<code style={{ background: '#e2e8f0', padding: '1px 3px', borderRadius: '3px' }}>update_stock=1</code>). Purchase Invoice created from PR is accounting only (<code style={{ background: '#e2e8f0', padding: '1px 3px', borderRadius: '3px' }}>update_stock=0</code>).
                                    </p>
                                </div>

                                {/* Option B: PO -> PI */}
                                <div
                                    onClick={() => setPurchaseWorkflow('PI_DIRECT')}
                                    style={{
                                        border: `2px solid ${purchaseWorkflow === 'PI_DIRECT' ? '#3b82f6' : '#e2e8f0'}`,
                                        background: purchaseWorkflow === 'PI_DIRECT' ? '#eff6ff' : '#ffffff',
                                        borderRadius: '0.75rem',
                                        padding: '1rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                        <span style={{ fontSize: '0.8rem', fontWeight: 900, color: purchaseWorkflow === 'PI_DIRECT' ? '#1d4ed8' : '#334155' }}>
                                            PO ➔ PI (Direct)
                                        </span>
                                        {purchaseWorkflow === 'PI_DIRECT' && <CheckCircle2 size={16} color="#3b82f6" />}
                                    </div>
                                    <p style={{ fontSize: '0.68rem', fontWeight: 700, color: '#475569', margin: 0, marginBottom: '6px' }}>
                                        Stock Updated Directly at Purchase Invoice (PI)
                                    </p>
                                    <p style={{ fontSize: '0.63rem', color: '#64748b', margin: 0, lineHeight: 1.3 }}>
                                        Purchase Invoice posts inventory ledger entries directly (<code style={{ background: '#e2e8f0', padding: '1px 3px', borderRadius: '3px' }}>update_stock=1</code>). Skips separate PR requirement.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Loyalty Program Configuration Card */}
                        <div className="so-table-card" style={{ padding: '1.5rem', border: `1.5px solid ${themeColor}30` }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ background: `${themeColor}15`, color: themeColor, padding: '8px', borderRadius: '10px' }}>
                                        <Award size={20} />
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--so-text-heading)', margin: 0 }}>Loyalty Points & Redemption Rules</h3>
                                        <p style={{ fontSize: '0.7rem', color: 'var(--so-text-muted)', margin: 0 }}>Define point-to-Dirham conversion rate and per-customer invoice redemption limits.</p>
                                    </div>
                                </div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <span style={{ fontSize: '0.75rem', fontWeight: 800, color: enableLoyaltyProgram ? '#10b981' : '#64748b' }}>
                                        {enableLoyaltyProgram ? 'ENABLED' : 'DISABLED'}
                                    </span>
                                    <input
                                        type="checkbox"
                                        checked={enableLoyaltyProgram}
                                        onChange={(e) => {
                                            const newVal = e.target.checked;
                                            setEnableLoyaltyProgram(newVal);
                                            localStorage.setItem('enable_loyalty_program', newVal ? 'true' : 'false');
                                            window.dispatchEvent(new Event('loyalty_setting_changed'));
                                            window.dispatchEvent(new Event('storage'));
                                        }}
                                        style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: themeColor }}
                                    />
                                </label>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.25rem' }}>
                                {/* Point Valuation */}
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--so-text-muted)', marginBottom: '6px' }}>
                                        Point Valuation (AED per 1 Point)
                                    </label>
                                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                        <input
                                            type="number"
                                            step="0.001"
                                            min="0"
                                            value={conversionFactor}
                                            onChange={(e) => setConversionFactor(e.target.value)}
                                            style={{
                                                width: '100%', height: '44px', borderRadius: '8px', border: '1px solid #cbd5e1',
                                                padding: '0 1rem', fontSize: '0.85rem', fontWeight: 700, color: '#0f172a', background: '#f8fafc'
                                            }}
                                            placeholder="0.01"
                                        />
                                        <span style={{ position: 'absolute', right: '12px', fontSize: '0.7rem', fontWeight: 800, color: '#64748b' }}>
                                            AED / PT
                                        </span>
                                    </div>
                                    <p style={{ fontSize: '0.65rem', color: '#64748b', marginTop: '6px', lineHeight: '1.3' }}>
                                        Example: <b>0.01</b> means 100 points = AED 1.00 discount.
                                    </p>
                                </div>

                                {/* Max Redemption Limit */}
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--so-text-muted)', marginBottom: '6px' }}>
                                        Max Redemption Limit (per Bill)
                                    </label>
                                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                        <input
                                            type="number"
                                            step="1"
                                            min="0"
                                            value={maxLoyaltyRedemption}
                                            onChange={(e) => setMaxLoyaltyRedemption(e.target.value)}
                                            style={{
                                                width: '100%', height: '44px', borderRadius: '8px', border: '1px solid #cbd5e1',
                                                padding: '0 1rem', fontSize: '0.85rem', fontWeight: 700, color: '#0f172a', background: '#f8fafc'
                                            }}
                                            placeholder="e.g. 50"
                                        />
                                        <span style={{ position: 'absolute', right: '12px', fontSize: '0.7rem', fontWeight: 800, color: '#64748b' }}>
                                            AED MAX
                                        </span>
                                    </div>
                                    <p style={{ fontSize: '0.65rem', color: '#64748b', marginTop: '6px', lineHeight: '1.3' }}>
                                        Max AED discount allowed per transaction. Set <b>0</b> for unlimited.
                                    </p>
                                </div>
                            </div>
                        </div>


                        {/* Mode of Payment Configuration Card */}
                        <div className="so-table-card" style={{ padding: '1.5rem', border: `1.5px solid ${themeColor}30` }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ background: `${themeColor}15`, color: themeColor, padding: '8px', borderRadius: '10px' }}>
                                        <CreditCard size={20} />
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--so-text-heading)', margin: 0 }}>Mode of Payment Settings</h3>
                                        <p style={{ fontSize: '0.7rem', color: 'var(--so-text-muted)', margin: 0 }}>Enable or disable available payment methods for POS Billing checkout screen.</p>
                                    </div>
                                </div>
                                <button
                                    onClick={handleSavePaymentModes}
                                    disabled={savingPaymentModes}
                                    style={{
                                        background: themeColor, color: '#fff', border: 'none', borderRadius: '8px',
                                        padding: '0.4rem 1rem', fontSize: '0.75rem', fontWeight: 800, cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', gap: '6px'
                                    }}
                                >
                                    {savingPaymentModes ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                                    Save Payment Modes
                                </button>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem' }}>
                                {paymentModes.map((mop) => (
                                    <div
                                        key={mop.name}
                                        onClick={() => togglePaymentMode(mop.name)}
                                        style={{
                                            border: `1.5px solid ${mop.enabled ? themeColor : '#e2e8f0'}`,
                                            background: mop.enabled ? `${themeColor}08` : '#f8fafc',
                                            borderRadius: '12px',
                                            padding: '1rem',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <div style={{
                                                width: '36px', height: '36px', borderRadius: '10px',
                                                background: mop.enabled ? themeColor : '#cbd5e1', color: '#ffffff',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900
                                            }}>
                                                {mop.name === 'Cash' ? <DollarSign size={18} /> :
                                                 mop.name.includes('Card') ? <CreditCard size={18} /> :
                                                 mop.name === 'InstaPay' ? <Banknote size={18} /> :
                                                 mop.name === 'Credit' ? <Coins size={18} /> : <Building2 size={18} />}
                                            </div>
                                            <div>
                                                <span style={{ fontSize: '0.82rem', fontWeight: 800, color: '#1e293b', display: 'block' }}>{mop.name}</span>
                                                <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>{mop.enabled ? 'ENABLED' : 'DISABLED'}</span>
                                            </div>
                                        </div>
                                        <label style={{ cursor: 'pointer' }} onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={!!mop.enabled}
                                                onChange={() => togglePaymentMode(mop.name)}
                                                style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: themeColor }}
                                            />
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>


                        {/* Keyboard Shortcuts Customization Card */}
                        <div className="so-table-card" style={{ padding: '1.5rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ background: `${themeColor}15`, color: themeColor, padding: '8px', borderRadius: '10px' }}>
                                        <SettingsIcon size={18} />
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--so-text-heading)', margin: 0 }}>Keyboard Shortcuts</h3>
                                        <p style={{ fontSize: '0.7rem', color: 'var(--so-text-muted)', margin: 0 }}>Configure personalized hotkeys for POS and Document Editors.</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => {
                                        Swal.fire({
                                            title: 'Reset Shortcuts?',
                                            text: 'Are you sure you want to restore all keyboard shortcuts to their factory defaults?',
                                            icon: 'warning',
                                            showCancelButton: true,
                                            confirmButtonText: 'Reset',
                                            confirmButtonColor: themeColor,
                                            cancelButtonColor: '#94a3b8'
                                        }).then(res => {
                                            if (res.isConfirmed) {
                                                resetAllShortcuts();
                                                Swal.fire('Reset!', 'All shortcuts have been reset.', 'success');
                                            }
                                        });
                                    }}
                                    style={{
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                        color: '#64748b',
                                        background: '#f1f5f9',
                                        border: 'none',
                                        padding: '6px 12px',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseOver={e => e.currentTarget.style.background = '#e2e8f0'}
                                    onMouseOut={e => e.currentTarget.style.background = '#f1f5f9'}
                                >
                                    Reset Defaults
                                </button>
                            </div>

                            {/* Section Selector */}
                            <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', marginBottom: '1rem', gap: '1rem' }}>
                                <button
                                    onClick={() => setActiveShortcutTab('pos_home')}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        borderBottom: activeShortcutTab === 'pos_home' ? `2px solid ${themeColor}` : '2px solid transparent',
                                        color: activeShortcutTab === 'pos_home' ? themeColor : '#64748b',
                                        fontWeight: 700,
                                        fontSize: '0.75rem',
                                        padding: '8px 4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    POS Home Screen
                                </button>
                                <button
                                    onClick={() => setActiveShortcutTab('doc_editor')}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        borderBottom: activeShortcutTab === 'doc_editor' ? `2px solid ${themeColor}` : '2px solid transparent',
                                        color: activeShortcutTab === 'doc_editor' ? themeColor : '#64748b',
                                        fontWeight: 700,
                                        fontSize: '0.75rem',
                                        padding: '8px 4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Form / Doc Editors
                                </button>
                            </div>

                            {/* List of shortcuts */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '350px', overflowY: 'auto', paddingRight: '4px' }}>
                                {Object.entries(shortcuts[activeShortcutTab] || {}).map(([actionId, currentKey]) => {
                                    const isRecording = recordingAction?.page === activeShortcutTab && recordingAction?.actionId === actionId;
                                    const label = ACTION_LABELS[activeShortcutTab]?.[actionId] || actionId;
                                    return (
                                        <div key={actionId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                                            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#334155' }}>
                                                {label}
                                            </div>
                                            <button
                                                onClick={() => setRecordingAction({ page: activeShortcutTab, actionId })}
                                                style={{
                                                    fontSize: '0.7rem',
                                                    fontWeight: 800,
                                                    padding: '6px 12px',
                                                    minWidth: '100px',
                                                    textAlign: 'center',
                                                    borderRadius: '6px',
                                                    border: isRecording ? `1px solid ${themeColor}` : '1px solid #cbd5e1',
                                                    background: isRecording ? `${themeColor}15` : 'white',
                                                    color: isRecording ? themeColor : '#475569',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                                }}
                                            >
                                                {isRecording ? 'Press Key...' : currentKey}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>


                        {/* System Summary Card */}
                        <div className="so-table-card" style={{ padding: '1.5rem', background: '#f8fafc' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b', marginBottom: '1rem' }}>
                                <Cpu size={16} />
                                <h4 style={{ fontSize: '0.75rem', fontWeight: 800, margin: 0, textTransform: 'uppercase' }}>Environmental Metadata</h4>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                                <div style={{ background: 'white', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                    <p style={{ fontSize: '0.6rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, marginBottom: '4px' }}>Active Company</p>
                                    <p style={{ fontSize: '0.8rem', fontWeight: 800, color: '#1e293b', margin: 0 }}>{company}</p>
                                </div>
                                <div style={{ background: 'white', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                    <p style={{ fontSize: '0.6rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, marginBottom: '4px' }}>Runtime Session</p>
                                    <p style={{ fontSize: '0.8rem', fontWeight: 800, color: '#1e293b', margin: 0 }}>Verified Secure</p>
                                </div>
                            </div>
                        </div>

                    </div>

                    {/* Secondary Info/Danger Column */}
                    <div style={{ gridColumn: 'span 5', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        
                        {/* Maintenance / Danger Zone */}
                        <div className="so-table-card" style={{ border: '1.5px solid #fee2e2', overflow: 'hidden' }}>
                            <div style={{ background: '#fef2f2', padding: '1.25rem', borderBottom: '1px solid #fee2e2', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <ShieldAlert size={20} color="#dc2626" />
                                <h3 style={{ fontSize: '0.85rem', fontWeight: 800, color: '#991b1b', margin: 0 }}>CORE MAINTENANCE</h3>
                            </div>
                            <div style={{ padding: '1.25rem' }}>
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <h4 style={{ fontSize: '0.75rem', fontWeight: 800, color: '#991b1b', marginBottom: '4px' }}>Cache Reconstruction</h4>
                                    <p style={{ fontSize: '0.7rem', color: '#b91c1c', lineHeight: '1.4' }}>Wipe item revisions and pricing caches. Useful if local values diverge from the central ERP.</p>
                                </div>
                                <button 
                                    onClick={handleForceReset}
                                    style={{ 
                                        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', 
                                        gap: '8px', padding: '0.75rem', background: '#dc2626', color: 'white', 
                                        borderRadius: '8px', border: 'none', fontWeight: 800, fontSize: '0.75rem',
                                        cursor: 'pointer', transition: 'opacity 0.2s'
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
                                    onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                                >
                                    <RotateCcw size={16} /> RESET TERMINAL CACHE
                                </button>
                            </div>
                        </div>

                        {/* Operational Card */}
                        <div style={{ background: themeLight, padding: '1.5rem', borderRadius: '12px', border: `1.5px dashed ${themeColor}40` }}>
                           <h4 style={{ color: themeColor, fontSize: '0.8rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem' }}>
                              <AlertCircle size={18} /> STATION NOTICE
                           </h4>
                           <div style={{ fontSize: '0.75rem', color: 'var(--so-text-body)', lineHeight: '1.6' }}>
                               <p style={{ marginBottom: '8px' }}>• <b>Virtual Stock:</b> Changes to the Warehouse source will update the "Virtual Stock" counters immediately.</p>
                               <p>• <b>Posting:</b> Book records on the ERP server only reconcile after a daily **POS Closing Entry** is submitted.</p>
                           </div>
                        </div>

                    </div>

                </main>
            </div>
        </div>
    );
};

export default Settings;
